import type { StoreServiceSwitches } from "@khazzin/api-client";
import { ErrorState } from "@khazzin/ui/patterns/page";
import { SkeletonText, Switch, useToast } from "@khazzin/ui/primitives";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";

/**
 * Settings → Services (bug list, Stores): the services this store's warehouse
 * offers it, each switched on or off by the store. Off hides the service and
 * refuses new work that needs it. Delivery off means the customer collects.
 */
const STORAGE = ["STORAGE", "RECEIVING", "LONG_TERM_STORAGE"];
const FULFILMENT = ["FULFILLMENT", "PICK_PACK", "PACKAGING"];

function groupOf(code: string): "storage" | "fulfilment" | "other" {
	return STORAGE.includes(code) ? "storage" : FULFILMENT.includes(code) ? "fulfilment" : "other";
}

function disabledAfter(switches: StoreServiceSwitches, code: string, on: boolean): string[] {
	const off = new Set(switches.services.filter((s) => !s.on).map((s) => s.service.code));
	if (!switches.deliveryOn) {
		off.add("DELIVERY");
	}
	if (on) {
		off.delete(code);
	} else {
		off.add(code);
	}
	return [...off].sort();
}

export function ServicesTab({ storeId, writable }: { storeId: string; writable: boolean }) {
	const { t } = useLocale();
	const api = useApi();
	const toast = useToast();
	const queryClient = useQueryClient();
	const key = ["portal", storeId, "services"] as const;
	const switches = useQuery({
		queryKey: key,
		queryFn: ({ signal }) => api.getPortalServiceSwitches({ signal }),
		enabled: storeId !== "",
	});
	const save = useMutation({
		mutationFn: (off: string[]) => api.setPortalServiceSwitches(off),
		onSuccess: (next) => queryClient.setQueryData(key, next),
		onError: (error) =>
			toast.error({ title: error instanceof Error ? error.message : String(error) }),
	});

	if (switches.isPending) {
		return <SkeletonText lines={6} />;
	}
	if (switches.isError) {
		return <ErrorState error={switches.error} t={t} onRetry={() => void switches.refetch()} />;
	}
	const data = switches.data;
	const flip = (code: string, on: boolean) => save.mutate(disabledAfter(data, code, on));
	const groups = ["storage", "fulfilment", "other"] as const;

	return (
		<div className="flex flex-col gap-5">
			<p className="text-[12.5px] text-fg-muted">{t("portal.settings.services.body")}</p>
			{groups.map((group) => {
				const rows = data.services.filter((s) => groupOf(s.service.code) === group);
				if (rows.length === 0) {
					return null;
				}
				return (
					<section
						key={group}
						aria-labelledby={`services-${group}`}
						className="flex flex-col gap-2"
					>
						<h3 id={`services-${group}`} className="text-[13px] font-bold">
							{t(`portal.settings.services.group.${group}`)}
						</h3>
						<ul className="flex flex-col divide-y divide-border rounded-md border border-border">
							{rows.map((row) => (
								<li key={row.service.code} className="px-3 py-2 text-[13px]">
									<Switch
										label={row.service.name}
										checked={row.on}
										disabled={!writable || save.isPending}
										onCheckedChange={(on) => flip(row.service.code, on)}
									/>
								</li>
							))}
						</ul>
					</section>
				);
			})}
			<section aria-labelledby="services-delivery" className="flex flex-col gap-2">
				<h3 id="services-delivery" className="text-[13px] font-bold">
					{t("portal.settings.services.group.delivery")}
				</h3>
				<div className="flex flex-col gap-1 rounded-md border border-border px-3 py-2 text-[13px]">
					<Switch
						label={t("portal.settings.services.delivery")}
						checked={data.deliveryOn}
						disabled={!writable || save.isPending}
						onCheckedChange={(on) => flip("DELIVERY", on)}
					/>
					<p className="text-[12px] text-fg-muted">{t("portal.settings.services.deliveryHint")}</p>
				</div>
			</section>
			{/* ADJ-1022-3: Khazzin sets it; the store sees what its new orders start as. */}
			<section aria-labelledby="services-default-mode" className="flex flex-col gap-2">
				<h3 id="services-default-mode" className="text-[13px] font-bold">
					{t("portal.settings.services.defaultMode")}
				</h3>
				<div className="flex flex-col gap-1 rounded-md border border-border px-3 py-2 text-[13px]">
					<p className="font-semibold">{t(`orderCards.service.${data.defaultServiceMode}`)}</p>
					<p className="text-[12px] text-fg-muted">
						{t("portal.settings.services.defaultModeHint")}
					</p>
				</div>
			</section>
		</div>
	);
}
